import { getApp } from 'firebase/app'
import { getFunctions, httpsCallable } from 'firebase/functions'

import { getFirebase, globalApi, listenFirebase, setFirebase, updateFirebase } from 'modules/App/globalApi'
import {
  STRIPE_CONNECT_DISCONNECT_FUNCTION,
  STRIPE_CONNECT_EXCHANGE_FUNCTION,
  getStripeConnectPendingStatePath,
} from 'modules/ProductInfo/utils/stripeConnect'

export const productInfoLibraryApi = globalApi.injectEndpoints({
  endpoints: (build) => ({
    listenStripePubKey: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/stripePubKey`, api)
      },
    }),
    // Records the one-time `state` nonce for a Stripe Connect attempt. The
    // callable compares the nonce Stripe hands back against this value before
    // it trusts the connection, then deletes it. The path is admin-only on
    // purpose; see getStripeConnectPendingStatePath.
    setStripeConnectPendingState: build.mutation({
      queryFn: async ({ coachOrgId, nonce }) => {
        const { data, error } = await setFirebase(getStripeConnectPendingStatePath(coachOrgId), {
          nonce,
          createdAt: Date.now(),
        })
        return { data, error }
      },
    }),
    // Hands Stripe's `code` and `state` to the callable that verifies the
    // nonce, exchanges the code with the platform secret key, and writes
    // stripeAccountId and stripePubKey into productInfo. The dashboard never
    // sees the secret key or the tokens; it only learns whether it worked.
    exchangeStripeConnect: build.mutation({
      queryFn: async ({ code, state }) => {
        try {
          const exchange = httpsCallable(getFunctions(getApp()), STRIPE_CONNECT_EXCHANGE_FUNCTION)
          const { data } = await exchange({ code, state })
          return { data }
        } catch (err) {
          return { error: { code: err?.code, message: err?.message, details: err?.details } }
        }
      },
    }),
    // Asks the callable to revoke Trybe's access to the org's Stripe account
    // and clear stripeAccountId and stripePubKey. The live listener on the
    // publishable key then brings the Connect button back on its own.
    disconnectStripeConnect: build.mutation({
      queryFn: async ({ coachOrgId }) => {
        try {
          const disconnect = httpsCallable(getFunctions(getApp()), STRIPE_CONNECT_DISCONNECT_FUNCTION)
          const { data } = await disconnect({ coachOrgId })
          return { data }
        } catch (err) {
          return { error: { code: err?.code, message: err?.message, details: err?.details } }
        }
      },
    }),
    listenStripeProductId: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/stripeProductId`, api)
      },
    }),
    listenLegacyProductPricing: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/plans`, api)
      },
    }),
    listenOneTimeProducts: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/oneTimeProducts`, api)
      },
    }),
    listenOneTimeProduct: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${productId}`, api)
      },
    }),
    listenOneTimeProductSlug: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${productId}/slug`, api)
      },
    }),
    listenOneTimeProductItemType: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${productId}/item/type`, api)
      },
    }),
    listenItemPreviewImg: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, itemId, itemType }, api) => {
        if (itemType === 'collection') {
          listenFirebase(`coachOrgs/${coachOrgId}/collections/${itemId}/previewImg`, api)
        } else if (itemType === 'program') {
          listenFirebase(`content/${coachOrgId}/programs/${itemId}/previewImg`, api)
        } else if (itemType === 'workout') {
          listenFirebase(`content/${coachOrgId}/workoutsLight/${itemId}/previewImg`, api)
        } else {
          listenFirebase(`coachOrgs/${coachOrgId}/videos/previewImg`, api)
        }
      },
    }),
    listenProductImg: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, id }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${id}/previewImg`, api)
      },
    }),
    listenProductInfoLogo: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/logo`, api)
      },
    }),
    listenProductPricing: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/subscriptionPlans`, api)
      },
    }),
    setProductPrices: build.mutation({
      queryFn: async ({ coachOrgId, plans }) => {
        const { data, error } = await setFirebase(`coachOrgs/${coachOrgId}/productInfo/subscriptionPlans`, plans)
        return { data, error }
      },
    }),
    setProductPrice: build.mutation({
      queryFn: async ({ coachOrgId, plan, planId }) => {
        const { data, error } = await setFirebase(
          `coachOrgs/${coachOrgId}/productInfo/subscriptionPlans/${planId}`,
          plan
        )
        return { data, error }
      },
    }),
    setOneTimeProduct: build.mutation({
      queryFn: async ({ coachOrgId, product, productId }) => {
        const { data, error } = await setFirebase(
          `coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${productId}`,
          product
        )
        return { data, error }
      },
    }),
    updateOneTimeProduct: build.mutation({
      queryFn: async ({ coachOrgId, product, productId }) => {
        const { data, error } = await updateFirebase(
          `coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${productId}`,
          product
        )
        return { data, error }
      },
    }),
    setProductPricePromos: build.mutation({
      queryFn: async ({ coachOrgId, planId, promos }) => {
        const { data, error } = await setFirebase(
          `coachOrgs/${coachOrgId}/productInfo/subscriptionPlans/${planId}/promo`,
          promos
        )
        return { data, error }
      },
    }),
    setProductPricePromo: build.mutation({
      queryFn: async ({ coachOrgId, planId, promoIdx, promo }) => {
        const { data, error } = await setFirebase(
          `coachOrgs/${coachOrgId}/productInfo/subscriptionPlans/${planId}/promo/${promoIdx}`,
          promo
        )
        return { data, error }
      },
    }),
    listenCoupons: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/coupons`, api)
      },
    }),
    setCoupon: build.mutation({
      queryFn: async ({ coachOrgId, couponKey, coupon }) => {
        const { data, error } = await setFirebase(`coachOrgs/${coachOrgId}/productInfo/coupons/${couponKey}`, coupon)
        return { data, error }
      },
    }),
    setStripeRequest: build.mutation({
      queryFn: async ({ coachOrgId, requestId, type, payload }) => {
        const { data: stripeAccountId } = await getFirebase(`coachOrgs/${coachOrgId}/productInfo/stripeAccountId`)
        const { data: stripeCustomerOwner } =
          (await getFirebase(`coachOrgs/${coachOrgId}/productInfo/stripeCustomerOwner`)) || 'connected_account'

        const value = {
          stripeAccountId,
          stripeCustomerOwner,
          coachOrgId,
          type,
          payload,
          response: {
            status: 'pending',
          },
        }

        const { data, error } = await setFirebase(
          `requests/stripeConnect/manageProducts/${coachOrgId}/requests/${requestId}`,
          value
        )
        return { data, error }
      },
    }),
    listenStripeResponse: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, responseId }, api) => {
        listenFirebase(`requests/stripeConnect/manageProducts/${coachOrgId}/responses/${responseId}`, api)
      },
    }),
    listenStripeProductResponse: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`requests/stripeConnect/manageProducts/${coachOrgId}/responses/stripeProductUpdate`, api)
      },
    }),
    listenStripeProductName: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/stripeProductName`, api)
      },
    }),
    listenProductCurrency: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/productInfo/defaultCurrency`, api)
      },
    }),
    setProductCurrency: build.mutation({
      queryFn: async ({ coachOrgId, currency }) => {
        const { data, error } = await setFirebase(`coachOrgs/${coachOrgId}/productInfo/defaultCurrency`, currency)
        return { data, error }
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useListenStripePubKeyQuery,
  useSetStripeConnectPendingStateMutation,
  useExchangeStripeConnectMutation,
  useDisconnectStripeConnectMutation,
  useListenStripeProductIdQuery,
  useListenLegacyProductPricingQuery,
  useListenProductPricingQuery,
  useListenOneTimeProductsQuery,
  useListenOneTimeProductQuery,
  useListenOneTimeProductSlugQuery,
  useListenOneTimeProductItemTypeQuery,
  useListenItemPreviewImgQuery,
  useListenProductImgQuery,
  useListenProductInfoLogoQuery,
  useSetProductPricesMutation,
  useSetProductPriceMutation,
  useSetOneTimeProductMutation,
  useUpdateOneTimeProductMutation,
  useSetProductPricePromosMutation,
  useSetProductPricePromoMutation,
  useListenCouponsQuery,
  useSetCouponMutation,
  useSetStripeRequestMutation,
  useListenStripeResponseQuery,
  useListenStripeProductResponseQuery,
  useListenStripeProductNameQuery,
  useListenProductCurrencyQuery,
  useSetProductCurrencyMutation,
} = productInfoLibraryApi
